import { useQuery } from "@tanstack/react-query";
import { DIRECT_DEBIT_QUERY_KEYS } from "../configs/queryKeys";
import { DirectDebitService } from "../services/directDebit.service";
import type { PaymentRequest } from "../types/directDebit.types";

/** How often a request still being carried out is read again: the CRM answers in seconds. */
const POLL_MS = 2000;
/** A payment still to make (a card on screen, a transfer to send) is read again more slowly. */
const WAITING_POLL_MS = 15000;

type DirectDebitQueryOptions = { enabled?: boolean };

export const useDirectDebitQuery = ({ enabled = true }: DirectDebitQueryOptions = {}) => useQuery({
  queryKey: DIRECT_DEBIT_QUERY_KEYS.situation(),
  queryFn: () => DirectDebitService.getSituation(),
  retry: false,
  enabled,
});

/** A new account asked for, read again until the CRM accepted or refused it. */
export const useBankAccountRequestQuery = (id: number | null) => useQuery({
  queryKey: DIRECT_DEBIT_QUERY_KEYS.bankAccountRequest(id ?? 0),
  queryFn: () => DirectDebitService.getBankAccountRequest(id ?? 0),
  enabled: id !== null,
  refetchInterval: (query) => (query.state.data?.status === "pending" ? POLL_MS : false),
});

const pollFor = (request: PaymentRequest | undefined): number | false => {
  if (!request || request.status === "pending") return POLL_MS;
  if (["awaiting_payment", "processing", "partially_paid"].includes(request.status)) return WAITING_POLL_MS;
  return false;
};

type PaymentRequestQueryOptions = { isPaused?: boolean };

/**
 * A payment asked for, read again until the CRM gave what is needed to pay, then until it is paid.
 * Pausing only holds back the reads of a request the CRM has not picked up: an answered one keeps its pace.
 */
export const usePaymentRequestQuery = (id: number | null, { isPaused = false }: PaymentRequestQueryOptions = {}) => useQuery({
  queryKey: DIRECT_DEBIT_QUERY_KEYS.paymentRequest(id ?? 0),
  queryFn: () => DirectDebitService.getPaymentRequest(id ?? 0),
  enabled: id !== null,
  refetchInterval: (query) => {
    const request = query.state.data;
    const isUnanswered = !request || request.status === "pending";
    return isPaused && isUnanswered ? false : pollFor(request);
  },
});
